<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Distribuicao Tempo de Resposta',
  },
  color: {
    type: String,
    default: '#3b82f6',
  },
});

const total = computed(() => {
  if (!props.data) return 0;
  return props.data.reduce((sum, d) => sum + d.count, 0);
});

const maxCount = computed(() => {
  if (!props.data || props.data.length === 0) return 1;
  return Math.max(...props.data.map(d => d.count), 1);
});

function getBarWidth(count) {
  return `${(count / maxCount.value) * 100}%`;
}

function getColorIntensity(index, total) {
  // Gradient from green to red
  const colors = [
    '#22c55e', // green - very fast
    '#84cc16', // lime
    '#eab308', // yellow
    '#f97316', // orange
    '#ef4444', // red - slow
    '#dc2626', // dark red
    '#991b1b', // very dark red
  ];
  return colors[Math.min(index, colors.length - 1)];
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span class="i-lucide-timer text-lg text-n-blue-10" />
        <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
      </div>
      <span class="text-sm text-n-slate-11">{{ total }} total</span>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!data || data.length === 0 || total === 0"
      class="flex items-center justify-center h-48 text-n-slate-11"
    >
      <span class="text-sm">Sem dados disponiveis</span>
    </div>

    <!-- Distribution Bars -->
    <div v-else class="flex flex-col gap-2">
      <div
        v-for="(item, index) in data"
        :key="item.label"
        class="flex items-center gap-3"
      >
        <span class="text-xs text-n-slate-11 w-20 flex-shrink-0 text-right">
          {{ item.label }}
        </span>
        <div class="flex-1 h-6 bg-n-alpha-1 rounded overflow-hidden relative">
          <div
            class="h-full rounded transition-all duration-300"
            :style="{
              width: getBarWidth(item.count),
              backgroundColor: getColorIntensity(index, data.length),
            }"
          />
          <span
            v-if="item.count > 0"
            class="absolute inset-y-0 left-2 flex items-center text-xs font-medium"
            :class="
              item.count > maxCount * 0.3 ? 'text-white' : 'text-n-slate-12'
            "
          >
            {{ item.count }}
          </span>
        </div>
        <span class="text-xs text-n-slate-10 w-12 text-right">
          {{ item.percentage }}%
        </span>
      </div>
    </div>
  </div>
</template>
